<template>
  <RsModal
    :id="id"
    v-model="input"
    :title="title"
    :button-text="saveButtonText"
    size="lg"
    lazy
    @shown="initPickr"
    @submit="submitData"
  >
    <div ref="pickrTarget" />
  </RsModal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Pickr from '@simonwep/pickr';
import '@simonwep/pickr/dist/themes/classic.min.css';

interface IColorPicker {
  hex: '',
}

interface IColorPickerNonReactive {
  _pickr: Pickr | undefined;
}

export default defineComponent({
  name: 'RsFormColorPicker',
  props: {
    value: {
      type: Boolean,
      default: false,
    },

    /**
     * Vue 3 v-model binding. Kept alongside the legacy `value`/`input` pair so
     * both `v-model` (modelValue/update:modelValue) and explicit `:value`+`@input`
     * consumers work. `undefined` means the parent did not bind v-model, so we
     * fall back to `value` (see the `boundValue` computed).
     */
    modelValue: {
      type: Boolean,
      default: undefined,
    },

    color: {
      type: Object as () => IColorPicker,
      default: () => ({ hex: '' }),
    },

    id: {
      type: String,
      default: 'id-color-picker',
    },

    title: {
      type: String,
      default: 'Background Colour',
    },

    hexLabel: {
      type: String,
      default: 'Hex',
    },

    saveButtonText: {
      type: String,
      default: 'Save',
    },
  },

  emits: ['input', 'on-submit', 'update:modelValue'],
  data() {
    return {
      input: this.modelValue !== undefined ? this.modelValue : this.value,
      colorPicker: { hex: '' },
    };
  },
  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): boolean {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
  },
  watch: {
    boundValue() {
      this.input = this.boundValue;
    },
    input() {
      this.$emit('input', this.input);
      this.$emit('update:modelValue', this.input);
    },
    color() {
      this.colorPicker = this.color;
      const self = this as typeof this & IColorPickerNonReactive;
      self._pickr?.setColor(this.color.hex || null);
    },
  },
  beforeUnmount() {
    const self = this as typeof this & IColorPickerNonReactive;
    self._pickr?.destroyAndRemove();
    self._pickr = undefined;
  },
  methods: {
    /**
     * (Re)initialize the Pickr instance once the modal's (lazy) content is actually
     * in the DOM. Doing this in `mounted` crashed under Vue 3: the target div
     * lives inside a `lazy` RsModal and isn't rendered/attached until the modal
     * is shown, so Pickr's `_finalBuild` read `null.parentElement`. Hooked to
     * RsModal's `@shown` event, which fires on every open — `lazy` destroys and
     * recreates the target div on each close/reopen, so any previous Pickr
     * instance (still attached to the now-removed old div) must be torn down
     * and rebuilt against the current one rather than reused.
     */
    initPickr(): void {
      const self = this as typeof this & IColorPickerNonReactive;
      const el = this.$refs.pickrTarget as HTMLElement | undefined;
      if (!el?.parentElement) return;
      self._pickr?.destroyAndRemove();
      self._pickr = Pickr.create({
        el,
        theme: 'classic',
        inline: true,
        showAlways: true,
        default: this.color.hex || '#ffffff',
        components: {
          preview: true,
          opacity: false,
          hue: true,
          interaction: {
            hex: true,
            input: true,
            save: false,
          },
        },
      });
      self._pickr.show();
      self._pickr.on('change', (hsvaColor: Pickr.HSVaColor) => {
        this.colorPicker.hex = hsvaColor.toHEXA().toString();
      });
    },
    submitData(): void {
      this.$emit('on-submit', this.colorPicker.hex);
    },
  },
});
</script>

<style lang="scss">
.pcr-app {
  position: static;
  box-shadow: none;
}

.pcr-button {
  display: none;
}
</style>
